// src/app/modules/Technical/pages/Machinery/SubcomponentModal.tsx
import React, { FC, useEffect, useState } from 'react'
import type { SubcomponentDto } from '../../core/_models'

interface SubcomponentModalProps {
  visible: boolean
  equipmentId: number
  editing?: SubcomponentDto
  onClose: () => void
  onSubmit: (data: SubcomponentDto) => void
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'
const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export const SubcomponentModal: FC<SubcomponentModalProps> = ({
  visible,
  equipmentId,
  editing,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<SubcomponentDto>({
    equipmentId: editing?.equipmentId ?? equipmentId,
    name: editing?.name || '',
    subComponentCode: editing?.subComponentCode || '',
    subComponentFunctionDescription: editing?.subComponentFunctionDescription || '',
    subComponentParticulars: editing?.subComponentParticulars || '',
    maker: (editing as any)?.maker || '',
    subComponentMultNumber: (editing as any)?.subComponentMultNumber ?? editing?.instanceCount ?? undefined,
    criticality: editing?.criticality ?? false,
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    if (editing) {
      setFormData({
        equipmentId: editing.equipmentId ?? equipmentId,
        name: editing.name,
        subComponentCode: editing.subComponentCode || '',
        subComponentFunctionDescription: editing.subComponentFunctionDescription || '',
        subComponentParticulars: editing.subComponentParticulars || '',
        maker: (editing as any)?.maker || '',
        subComponentMultNumber: (editing as any)?.subComponentMultNumber ?? editing?.instanceCount ?? undefined,
        criticality: editing.criticality ?? false,
      })
    } else {
      setFormData({
        equipmentId,
        name: '',
        subComponentCode: '',
        subComponentFunctionDescription: '',
        subComponentParticulars: '',
        maker: '',
        subComponentMultNumber: undefined,
        criticality: false,
      })
    }
    setErrors({})
  }, [editing, equipmentId])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: { [key: string]: string } = {}
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }
    setErrors(newErrors)
    if (Object.keys(newErrors).length === 0) {
      onSubmit(formData)
    }
  }

  if (!visible) return null

  return (
    <div style={WRAP_STYLE} tabIndex={-1} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{ width: '600px', maxWidth: '90vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <h5 className='modal-title text-dark mx-6 mt-6'>{editing ? 'Edit Subcomponent' : 'Add Subcomponent'}</h5>
          <button type='button' className='btn-close mx-6 mt-6' onClick={onClose}></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div
            className='flex-grow-1 overflow-auto px-4 py-3 m-6'
            style={{ maxHeight: '70vh' }}
          >
            <div className='mb-5'>
              <label className={LABEL}>
                Subsystem Name <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className={`form-control text-dark ${errors.name ? 'is-invalid' : ''}`}
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value })
                  if (errors.name) setErrors({ ...errors, name: '' })
                }}
                placeholder='e.g., Fuel Injector, Piston Ring'
              />
              {errors.name && <div className='invalid-feedback'>{errors.name}</div>}
            </div>

            {/* Subsystem Code */}
            <div className='mb-5'>
              <label className={LABEL}>Subsystem Code</label>
              <input
                type='text'
                className='form-control text-dark'
                value={formData.subComponentCode || ''}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 12) // Max 12 chars
                  setFormData({ ...formData, subComponentCode: value })
                }}
                placeholder='Subsystem code'
                maxLength={12}
              />
              <div className='form-text text-muted'>Max 12 characters</div>
            </div>

            {/* Subsystem Description */}
            <div className='mb-5'>
              <label className={LABEL}>Subsystem Description</label>
              <input
                type='text'
                className='form-control text-dark'
                value={formData.subComponentFunctionDescription || ''}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 100) // Max 100 chars
                  setFormData({ ...formData, subComponentFunctionDescription: value })
                  if (!formData.name) {
                    setFormData(prev => ({ ...prev, name: value }))
                  }
                }}
                placeholder='Subsystem description'
                maxLength={100}
              />
              <div className='form-text text-muted'>Max 100 characters</div>
            </div>

            {/* Subsystem Maker */}
            <div className='mb-5'>
              <label className={LABEL}>Subsystem Maker</label>
              <input
                type='text'
                className='form-control text-dark'
                value={formData.maker || ''}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 100)
                  setFormData({ ...formData, maker: value })
                }}
                placeholder='Subsystem maker'
                maxLength={100}
              />
              <div className='form-text text-muted'>Max 100 characters</div>
            </div>

            {/* Mult No */}
            <div className='mb-5'>
              <label className={LABEL}>Mult No</label>
              <input
                type='number'
                className='form-control text-dark'
                value={formData.subComponentMultNumber ?? ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : Number(e.target.value)
                  setFormData({ ...formData, subComponentMultNumber: value })
                }}
                placeholder='Multiplicity'
                min={1}
              />
              <div className='form-text text-muted'>Optional. Creates subcomponent instances.</div>
            </div>

            {/* Subsystem Particulars */}
            <div className='mb-5'>
              <label className={LABEL}>Subsystem Particulars</label>
              <textarea
                className='form-control text-dark'
                rows={4}
                value={formData.subComponentParticulars || ''}
                onChange={(e) => setFormData({ ...formData, subComponentParticulars: e.target.value })}
                placeholder='Detailed subsystem particulars (optional)'
              />
              <div className='form-text text-muted'>Max 2000 characters</div>
            </div>

            {/* Criticality */}
            <div className='mb-5'>
              <label className={LABEL}>Critical Sub Component (Yes/No)</label>
              <div className='d-flex gap-3 align-items-center'>
                <div className='form-check'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='criticality'
                    id='subCriticalYes'
                    checked={!!formData.criticality}
                    onChange={() => setFormData({ ...formData, criticality: true })}
                  />
                  <label className='form-check-label' htmlFor='subCriticalYes'>
                    Yes
                  </label>
                </div>
                <div className='form-check'>
                  <input
                    className='form-check-input'
                    type='radio'
                    name='criticality'
                    id='subCriticalNo'
                    checked={!formData.criticality}
                    onChange={() => setFormData({ ...formData, criticality: false })}
                  />
                  <label className='form-check-label' htmlFor='subCriticalNo'>
                    No
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className='border-top d-flex justify-content-end gap-2 px-4 py-3 flex-shrink-0 m-6'>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
              Cancel
            </button>
            <button type='submit' className='btn btn-primary btn-sm'>
              {editing ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
